import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserIcon,
  BriefcaseIcon,
  UsersIcon,
  FlagIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { useAdminStats } from "../hooks/useAdminStats";
import { StatsCard } from "../components/StatsCard";

export const AdminDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stats, documentStats, loading, error, refetch } = useAdminStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-tiber-600 text-white rounded-lg hover:bg-tiber-600/90 transition-colors"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-tiber-900">
          {t("admin.dashboard.title")}
        </h1>
        <p className="text-sm font-light text-tiber-600/60 mt-1">
          {t("admin.dashboard.subtitle")}
        </p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title={t("admin.dashboard.stats.totalUsers")}
          value={stats?.totalUsers || 0}
          icon={UserGroupIcon}
          color="tiber"
        />
        <StatsCard
          title={t("admin.dashboard.stats.activeUsers")}
          value={stats?.activeUsers || 0}
          icon={ChartBarIcon}
          color="green"
        />
        <StatsCard
          title={t("admin.dashboard.stats.totalDocuments")}
          value={stats?.totalDocuments || 0}
          icon={DocumentTextIcon}
          color="tiber"
        />
        <StatsCard
          title={t("admin.dashboard.stats.documentsToday")}
          value={stats?.documentsToday || 0}
          icon={DocumentTextIcon}
          color="yellow"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <h2 className="text-lg font-bold text-tiber-900 mb-4">
            {t("admin.dashboard.usersByType")}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-tiber-50 rounded-lg">
                  <UserIcon className="w-5 h-5 text-tiber-600" />
                </div>
                <span className="text-tiber-900 font-medium">
                  {t("admin.dashboard.lawyers")}
                </span>
              </div>
              <span className="text-lg font-bold text-tiber-900">
                {stats?.usersByType.lawyer || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-tiber-50 rounded-lg">
                  <BriefcaseIcon className="w-5 h-5 text-tiber-600" />
                </div>
                <span className="text-juridico font-medium">
                  {t("admin.dashboard.firms")}
                </span>
              </div>
              <span className="text-lg font-bold text-juridico">
                {stats?.usersByType.firm || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <UsersIcon className="w-5 h-5 text-yellow-600" />
                </div>
                <span className="text-juridico font-medium">
                  {t("admin.dashboard.clients")}
                </span>
              </div>
              <span className="text-lg font-bold text-juridico">
                {stats?.usersByType.client || 0}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Users by Country */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <h2 className="text-lg font-bold text-juridico mb-4">
            {t("admin.dashboard.usersByCountry")}
          </h2>
          <div className="space-y-3">
            {Object.entries(stats?.usersByCountry || {})
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .slice(0, 5)
              .map(([country, count]) => (
                <div
                  key={country}
                  className="flex items-center justify-between"
                >
                  <span className="text-juridico font-medium">{country}</span>
                  <span className="text-lg font-bold text-juridico">
                    {count as number}
                  </span>
                </div>
              ))}
          </div>
        </motion.div>
      </div>

      {/* Documents Stats */}
      {documentStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <h2 className="text-lg font-bold text-juridico mb-4">
            {t("admin.dashboard.documentStats")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* By Country */}
            <div>
              <h3 className="text-sm font-semibold text-juridico/60 mb-3">
                {t("admin.dashboard.byCountry")}
              </h3>
              <div className="space-y-2">
                {Object.entries(documentStats.byCountry || {})
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .slice(0, 5)
                  .map(([country, count]) => (
                    <div key={country} className="flex justify-between text-sm">
                      <span className="text-juridico">{country}</span>
                      <span className="font-semibold text-juridico">
                        {count as number}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* By Status */}
            <div>
              <h3 className="text-sm font-semibold text-juridico/60 mb-3">
                {t("admin.dashboard.byStatus")}
              </h3>
              <div className="space-y-2">
                {Object.entries(documentStats.byStatus || {}).map(
                  ([status, count]) => (
                    <div key={status} className="flex justify-between text-sm">
                      <span className="text-juridico capitalize">{status}</span>
                      <span className="font-semibold text-juridico">
                        {count as number}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Top Types */}
            <div>
              <h3 className="text-sm font-semibold text-juridico/60 mb-3">
                {t("admin.dashboard.topTypes")}
              </h3>
              <div className="space-y-2">
                {Object.entries(documentStats.byType || {})
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .slice(0, 5)
                  .map(([type, count]) => (
                    <div key={type} className="flex justify-between text-sm">
                      <span className="text-juridico truncate" title={type}>
                        {type}
                      </span>
                      <span className="font-semibold text-juridico ml-2">
                        {count as number}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <h2 className="text-lg font-bold text-juridico mb-4">
          {t("admin.dashboard.quickActions")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t("admin.dashboard.manageCases"), href: "/admin/cases", icon: BriefcaseIcon, color: "tiber" },
            { label: t("admin.dashboard.moderateDocuments"), href: "/admin/documents-moderation", icon: DocumentTextIcon, color: "tiber" },
            { label: t("admin.dashboard.manageUsers"), href: "/admin/users", icon: UserGroupIcon, color: "tiber" },
            { label: t("admin.dashboard.viewReports"), href: "/admin/reports", icon: FlagIcon, color: "tiber" },
          ].map((action) => (
            <button
              key={action.href}
              onClick={() => navigate(action.href)}
              className="flex items-center gap-3 p-4 bg-white rounded-lg border border-romance-200 hover:border-tiber-300 hover:bg-tiber-50/50 transition-all text-left group cursor-pointer"
            >
              <div className="p-2 bg-tiber-50 rounded-lg group-hover:bg-tiber-100 transition-colors">
                <action.icon className="w-5 h-5 text-tiber-600" />
              </div>
              <span className="text-sm font-medium text-juridico flex-1">{action.label}</span>
              <ArrowRightIcon className="w-4 h-4 text-tiber-400 group-hover:text-tiber-600 transition-colors" />
            </button>
          ))}
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-linear-to-r from-tiber-600 to-tiber-900 rounded-lg p-6 text-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-white/80 text-sm font-medium">
              {t("admin.dashboard.newUsersThisWeek")}
            </p>
            <p className="text-3xl font-bold mt-1">
              {stats?.newUsersThisWeek || 0}
            </p>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">
              {t("admin.dashboard.totalRevenue")}
            </p>
            <p className="text-3xl font-bold mt-1">
              ${(stats?.totalRevenue || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">
              {t("admin.dashboard.documentsToday")}
            </p>
            <p className="text-3xl font-bold mt-1">
              {stats?.documentsToday || 0}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
